import React from 'react';
import { Link } from 'react-router-dom';
import AgreementPageLayout from '../../components/AgreementPageLayout';
import { BUSINESS_CHALLENGE_MINIMUM_NOTICE } from '../../utils/businessChallengeCopy';

const BusinessChallengeOfficialRulesPage: React.FC = () => {
  return (
    <AgreementPageLayout
      title="AACore Build Your Business Challenge Official Rules"
      effectiveDate="09/20/2026"
      lastUpdated="09/20/2026"
      toc={[
        { id: 'operator-contact', title: 'Sponsor & Contact' },
        { id: 'overview', title: '1) Overview' },
        { id: 'eligibility', title: '2) Eligibility' },
        { id: 'how-to-enter', title: '3) How to Enter' },
        { id: 'entry-limits', title: '4) Entry Limits and Qualifying Entries' },
        { id: 'minimum-participation', title: '5) Minimum Participation Requirement' },
        { id: 'prizes', title: '6) Prizes' },
        { id: 'selection', title: '7) Winner Selection and Notification' },
        { id: 'odds', title: '8) Odds' },
        { id: 'taxes', title: '9) Taxes and Winner Responsibilities' },
        { id: 'publicity', title: '10) Publicity and Use of Submissions' },
        { id: 'disqualification', title: '11) Disqualification' },
        { id: 'privacy', title: '12) Privacy' },
        { id: 'general', title: '13) General Conditions' },
        { id: 'acknowledgment', title: 'Acknowledgment' },
      ]}
    >
      <p>
        These Official Rules govern the AACore Build Your Business Challenge (the &ldquo;Challenge&rdquo;). By submitting an
        entry, you agree to these Official Rules and the{' '}
        <Link to="/privacy">AACore Privacy Policy</Link>.
      </p>
      <p>
        <Link to="/business-challenge">Return to the Business Challenge page</Link>.
      </p>

      <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <div className="font-semibold mb-1">Minimum Participation Requirement</div>
        {BUSINESS_CHALLENGE_MINIMUM_NOTICE}
      </div>

      <div id="operator-contact" className="mt-6 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
        <div className="font-semibold text-gray-900 mb-1">Sponsor &amp; Contact</div>
        <strong>Challenge:</strong> AACore Build Your Business Challenge<br />
        <strong>Sponsor:</strong> A&amp;A Inomatic, LLC (&ldquo;AAI,&rdquo; &ldquo;Sponsor,&rdquo; &ldquo;we,&rdquo;
        &ldquo;us,&rdquo; &ldquo;our&rdquo;)<br />
        <strong>Platform:</strong> AACore Booking (<a href="https://booking.aacore.net">booking.aacore.net</a>)<br />
        <strong>Contact:</strong> <a href="mailto:support@aainomatic.com">support@aainomatic.com</a><br />
        <strong>Mailing Address:</strong> Clifton, VA 20124
      </div>

      <h3 id="overview">1) Overview</h3>
      <p>
        The Challenge invites freelance and small service businesses to create a free AACore Booking business account or
        move an existing business to AACore, build a booking page, and submit a progress entry for a chance to win part of
        a $3,000 prize package. The Challenge is a skill-based promotion. Progress, completeness, and customer-readiness
        matter more than audience size.
      </p>
      <p>
        Entry deadline: November 15, 2026. Intended winner announcement: Thanksgiving Day, November 26, 2026, unless the
        Challenge is canceled under Section 5. No purchase is necessary to enter or win. Creating an AACore Booking
        business account is free during the applicable trial or enhancement period. Void where prohibited.
      </p>

      <h3 id="eligibility">2) Eligibility</h3>
      <p>The Challenge is open to individuals and entities that:</p>
      <ul>
        <li>operate or are starting a freelance or small service business;</li>
        <li>create a free AACore Booking business account or move an existing service business to AACore Booking;</li>
        <li>submit a complete entry as described in these Official Rules by the deadline; and</li>
        <li>can accept a prize in compliance with applicable law.</li>
      </ul>
      <p>
        Employees, officers, and contractors of A&amp;A Inomatic, LLC, and members of their immediate households, are not
        eligible. The Challenge is void where prohibited by law.
      </p>

      <h3 id="how-to-enter">3) How to Enter</h3>
      <p>To enter:</p>
      <ol>
        <li>
          Create a free AACore Booking business account at{' '}
          <Link to="/business-registration/">/business-registration/</Link>, or use an existing AACore business account if
          you are moving your business to AACore.
        </li>
        <li>Build your booking page, including services, schedule, photos, policies, and business details as applicable.</li>
        <li>
          Submit the official entry form on the{' '}
          <Link to="/business-challenge">Business Challenge page</Link> by November 15, 2026. Use the email connected to
          your AACore business account and include your AACore business-page URL, a description of what you built, and how
          a prize would support your next step.
        </li>
      </ol>
      <p>
        An optional public supporting link (video, portfolio, or social profile) may be included. A short 30 to 60 second
        introduction is welcome but not required.
      </p>

      <h3 id="entry-limits">4) Entry Limits and Qualifying Entries</h3>
      <p>
        Limit one entry per business. A &ldquo;qualifying entry&rdquo; is a complete, eligible entry from a distinct
        business that complies with these Official Rules. Duplicate, incomplete, fraudulent, or ineligible entries will
        not count toward the minimum participation requirement and will not be eligible to win.
      </p>
      <p>
        Sponsor may treat multiple submissions from the same business, the same AACore business account, or submissions
        that appear to be from the same operator as a single entry or as non-qualifying, in Sponsor&apos;s reasonable
        discretion.
      </p>

      <h3 id="minimum-participation">5) Minimum Participation Requirement</h3>
      <p>{BUSINESS_CHALLENGE_MINIMUM_NOTICE}</p>
      <p>
        If the Challenge is canceled under this section, no prizes will be awarded, no alternate promotion is promised,
        and stored entries will be retained only as needed to notify entrants, administer the cancellation, and comply
        with law and the Privacy Policy. Existing entries received before these Official Rules were posted remain stored
        and, if they are complete and otherwise eligible, will count toward the 100-entry minimum as one entry per
        distinct business.
      </p>

      <h3 id="prizes">6) Prizes</h3>
      <p>If the minimum participation requirement is met, the advertised prize package totals $3,000:</p>
      <ul>
        <li>Grand Prize: $1,500</li>
        <li>Second Prize: $750</li>
        <li>Third Prize: $500</li>
        <li>People&apos;s Choice: $250</li>
      </ul>
      <p>
        Prizes are awarded as stated by Sponsor (for example, payment to the winner). No substitution, assignment, or
        transfer of a prize is permitted except by Sponsor, who may substitute a prize of equal or greater value if a
        prize becomes unavailable. Prize values are in U.S. dollars. Any difference between advertised and actual value
        will not be awarded.
      </p>

      <h3 id="selection">7) Winner Selection and Notification</h3>
      <p>
        If the Challenge proceeds, Sponsor will review qualifying entries for a complete business page, a clear customer
        experience, demonstrated effort and improvement, and a useful next-step plan. People&apos;s Choice, if used, may
        include a public or community component announced on the Challenge page. Winners are intended to be announced on
        Thanksgiving Day, November 26, 2026.
      </p>
      <p>
        Potential winners will be notified using the email provided on the entry. A potential winner may be required to
        confirm eligibility, complete an affidavit or tax form, and provide payment details reasonably requested by
        Sponsor. If a potential winner cannot be contacted, does not respond within the time Sponsor specifies, or is
        found ineligible, Sponsor may select an alternate or leave that prize unawarded.
      </p>

      <h3 id="odds">8) Odds</h3>
      <p>
        Odds of winning depend on the number of qualifying entries received and the relative quality of those entries.
        This is not a game of chance.
      </p>

      <h3 id="taxes">9) Taxes and Winner Responsibilities</h3>
      <p>
        Winners are solely responsible for any taxes, reporting, and other costs associated with a prize. Sponsor may
        issue tax forms where required. Acceptance of a prize is conditioned on compliance with these Official Rules and
        applicable law.
      </p>

      <h3 id="publicity">10) Publicity and Use of Submissions</h3>
      <p>
        Except where prohibited, entry constitutes permission for Sponsor to use the entrant&apos;s name, business name,
        business-page URL, likeness, and non-confidential submission content for administration and for announcing
        winners and discussing the Challenge, without further compensation. You represent that your submission is your
        own and does not infringe the rights of others.
      </p>

      <h3 id="disqualification">11) Disqualification</h3>
      <p>
        Sponsor may disqualify any entry that is late, incomplete, copied, misleading, fraudulent, submitted through
        automated means, or that otherwise violates these Official Rules or AACore account terms. Sponsor&apos;s
        decisions on eligibility, qualifying entries, and winners are final.
      </p>

      <h3 id="privacy">12) Privacy</h3>
      <p>
        Information submitted with an entry is used to administer the Challenge, contact entrants, announce winners or
        cancellation, and as described in the <Link to="/privacy">AACore Privacy Policy</Link>.
      </p>

      <h3 id="general">13) General Conditions</h3>
      <p>
        Sponsor is not responsible for lost, late, incomplete, or misdirected entries, or for technical failures that
        prevent submission or notification. Sponsor may modify, suspend, or cancel the Challenge if fraud, technical
        failure, or other causes beyond Sponsor&apos;s reasonable control corrupt administration, or as stated in
        Section 5. These Official Rules are governed by the laws of the Commonwealth of Virginia, without regard to
        conflict-of-law rules, except that any mandatory consumer-protection law of your place of residence continues
        to apply. Courts located in Virginia shall have exclusive jurisdiction over disputes arising from the Challenge,
        except where prohibited.
      </p>
      <p>
        These Official Rules do not change the AACore Business Account Agreement or other platform terms that apply to
        your AACore account.
      </p>

      <h3 id="acknowledgment">Acknowledgment</h3>
      <p>
        By submitting an entry and checking the confirmation box on the entry form, you confirm that the information is
        accurate and that you have read and agree to these Official Rules, including the Minimum Participation
        Requirement, and the AACore Privacy Policy.
      </p>
    </AgreementPageLayout>
  );
};

export default BusinessChallengeOfficialRulesPage;
